import Link from 'next/link';
import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';
import {
  ArrowRight,
  CalendarDays,
  Clock3,
  Search,
  UserRound,
} from 'lucide-react';
import { useState } from 'react';
import SiteLayout from '@/components/site/SiteLayout';
import { blogArticles } from '@/data/blog';
import { getAbsoluteUrl, SITE_URL } from '@/lib/seoRoutes';

const topics = [
  'All Topics',
  ...Array.from(new Set(blogArticles.map((article) => article.category))),
];

const BlogPage = () => {
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState('');
  const queryCategory =
    typeof router.query.category === 'string' ? router.query.category : '';
  const activeTopic =
    router.isReady && topics.includes(queryCategory)
      ? queryCategory
      : 'All Topics';
  const normalizedSearch = searchQuery.trim().toLowerCase();
  const filteredArticles = blogArticles.filter((article) => {
    const matchesTopic =
      activeTopic === 'All Topics' || article.category === activeTopic;
    const searchableText = [
      article.title,
      article.excerpt,
      article.category,
      article.author,
      article.role,
      ...article.sections.flatMap((section) => [
        section.heading || '',
        ...(section.body || []),
        ...(section.bullets || []),
      ]),
    ]
      .join(' ')
      .toLowerCase();

    return matchesTopic && searchableText.includes(normalizedSearch);
  });
  const [featuredArticle, ...secondaryArticles] = filteredArticles;

  const handleTopicClick = (topic: string) => {
    router.push(
      {
        pathname: '/blog',
        query: topic === 'All Topics' ? {} : { category: topic },
      },
      undefined,
      { shallow: true },
    );
  };

  const scrollToArticles = () => {
    document
      .getElementById('articles')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleFacilityArticles = () => {
    setSearchQuery('');
    handleTopicClick('Facility Operations');
    setTimeout(scrollToArticles, 0);
  };

  const handleDoctorArticles = () => {
    handleTopicClick('All Topics');
    setSearchQuery('physician');
    setTimeout(scrollToArticles, 0);
  };

  return (
    <SiteLayout
      title='DocMoonlight Blogs | Contrast Coverage and Healthcare Staffing Insights'
      description='Read DocMoonlight blogs and articles about contrast coverage, imaging center staffing, virtual supervision, onsite coverage, and healthcare operations.'
      keywords={[
        'DocMoonlight blogs',
        'DocMoonlight blog',
        'contrast coverage blog',
        'imaging center staffing',
        'healthcare operations',
        'virtual contrast supervision',
      ]}
    >
      <Head>
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Blog',
              name: 'DocMoonlight Blogs',
              url: getAbsoluteUrl('/blog'),
              description:
                'DocMoonlight blogs and articles about contrast coverage, imaging center staffing, virtual supervision, onsite coverage, and healthcare operations.',
              publisher: {
                '@type': 'Organization',
                name: 'DocMoonlight',
                url: SITE_URL,
                logo: {
                  '@type': 'ImageObject',
                  url: getAbsoluteUrl('/images/logo.svg'),
                },
              },
              blogPost: blogArticles.map((article) => ({
                '@type': 'BlogPosting',
                headline: article.title,
                url: getAbsoluteUrl(`/blog/${article.slug}`),
                description: article.excerpt,
                datePublished: article.date,
                author: {
                  '@type': 'Organization',
                  name: article.author,
                },
              })),
            }),
          }}
        />
      </Head>
      <div className='w-full bg-[#f7f8fa] text-[#111b35]'>
        <main>
          <section className='relative left-1/2 w-screen -translate-x-1/2 overflow-hidden bg-[#f4f5f7]'>
            <div className='pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_72%_22%,rgba(15,23,42,0.08),transparent_34%),radial-gradient(circle_at_28%_78%,rgba(245,158,11,0.18),transparent_28%)]' />
            <div className='relative w-full px-5 pb-16 pt-20 text-center lg:px-12 lg:pb-20 lg:pt-28'>
              <div className='inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-black uppercase tracking-[0.16em] text-[#111b35] shadow-sm'>
                <span className='h-2 w-2 rounded-full bg-[#f59e0b]' />
                Blog & Resources
              </div>

              <h1 className='mx-auto mt-8 max-w-4xl text-[3rem] font-black tracking-tight text-[#111b35] md:text-[5rem]'>
                Mastering the Modern
                <span className='block bg-[linear-gradient(90deg,#1b2132_0%,#f59e0b_78%)] bg-clip-text text-transparent'>
                  Radiology Practice
                </span>
              </h1>

              <p className='mx-auto mt-6 max-w-2xl text-lg font-semibold leading-8 text-slate-600 md:text-xl'>
                Practical strategies on staffing, contrast coverage, and
                workflow efficiency for imaging centers optimizing performance
                and physicians in training navigating modern flexible clinical
                opportunities.
              </p>

              <div className='mt-9 flex flex-col items-center justify-center gap-4 sm:flex-row'>
                <button
                  type='button'
                  onClick={handleFacilityArticles}
                  className='inline-flex min-h-[58px] min-w-[270px] items-center justify-center gap-3 rounded-2xl bg-[#111b35] px-8 text-base font-black text-white shadow-[0_18px_38px_rgba(15,23,42,0.2)] transition hover:-translate-y-1'
                >
                  Explore Facility Articles
                  <ArrowRight className='h-4 w-4 text-[#f59e0b]' />
                </button>
                <button
                  type='button'
                  onClick={handleDoctorArticles}
                  className='inline-flex min-h-[58px] min-w-[270px] items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-white px-8 text-base font-black text-[#111b35] shadow-[0_14px_30px_rgba(15,23,42,0.08)] transition hover:-translate-y-1'
                >
                  Explore Physician Articles
                  <UserRound className='h-4 w-4' />
                </button>
              </div>

              <div className='mx-auto mt-16 flex max-w-[42rem] items-center gap-3 rounded-full border border-slate-200 bg-white px-7 py-5 text-left shadow-[0_18px_46px_rgba(15,23,42,0.08)]'>
                <Search className='h-5 w-5 shrink-0 text-slate-400' />
                <input
                  type='search'
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder='Search operational guides, coverage tips...'
                  className='w-full border-0 bg-transparent text-lg font-semibold text-[#111b35] outline-none ring-0 placeholder:text-slate-400 focus:border-0 focus:outline-none focus:ring-0'
                />
              </div>
            </div>
          </section>

          <section className='px-5 pb-20 lg:px-8 mt-2'>
            <div className='w-full'>
              <div className='flex flex-wrap gap-3'>
                {topics.map((topic) => (
                  <button
                    key={topic}
                    type='button'
                    onClick={() => handleTopicClick(topic)}
                    className={`min-h-11 rounded-full border px-7 text-sm font-black transition hover:-translate-y-0.5 ${
                      activeTopic === topic
                        ? 'border-[#111b35] bg-[#111b35] text-white shadow-[0_12px_24px_rgba(15,23,42,0.14)]'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-[#f59e0b] hover:text-[#111b35]'
                    }`}
                  >
                    {topic}
                  </button>
                ))}
              </div>

              <div id='articles' className='mt-24'>
                {featuredArticle ? (
                  <Link
                    href={`/blog/${featuredArticle.slug}`}
                    className='grid gap-8 rounded-[2rem] border border-slate-200 bg-white p-6 shadow-[0_24px_70px_rgba(15,23,42,0.08)] transition hover:-translate-y-1 hover:shadow-[0_28px_76px_rgba(15,23,42,0.12)] lg:grid-cols-[0.94fr_1fr] lg:p-8'
                  >
                    <div className='relative min-h-[22rem] overflow-hidden rounded-[1.45rem] bg-slate-200'>
                      <Image
                        src={featuredArticle.image}
                        alt=''
                        fill
                        sizes='(max-width: 1023px) 100vw, 48vw'
                        className='absolute inset-0 h-full w-full object-cover'
                      />
                      <div className='absolute inset-0 bg-[linear-gradient(180deg,transparent_35%,rgba(15,23,42,0.55)_100%)]' />
                      <div className='absolute bottom-5 left-5 rounded-lg bg-[#f59e0b] px-4 py-2 text-xs font-black uppercase tracking-wide text-white'>
                        {featuredArticle.category}
                      </div>
                    </div>

                    <div className='flex flex-col justify-center py-2 lg:py-6'>
                      <div className='flex flex-wrap items-center gap-4 text-sm font-semibold text-slate-500'>
                        <span className='inline-flex items-center gap-2'>
                          <Clock3 className='h-4 w-4' />
                          {featuredArticle.readTime}
                        </span>
                        <span className='text-slate-300'>.</span>
                        <span>{featuredArticle.date}</span>
                      </div>

                      <h2 className='mt-5 max-w-3xl text-[2rem] font-black leading-[1.02] tracking-tight text-[#111b35] md:text-[2.7rem]'>
                        {featuredArticle.title}
                      </h2>
                      <p className='mt-6 max-w-3xl text-lg font-semibold leading-8 text-slate-600'>
                        {featuredArticle.excerpt}
                      </p>

                      <div className='mt-10 flex items-center justify-between gap-5'>
                        <div className='flex items-center gap-4'>
                          <div className='h-12 w-12 overflow-hidden rounded-full border border-slate-200 bg-white'>
                            <Image
                              src='/images/docmoonlight-mark.svg'
                              alt='DocMoonlight'
                              width={48}
                              height={48}
                              sizes='48px'
                              className='h-full w-full object-contain p-2'
                            />
                          </div>
                          <div>
                            <div className='text-sm font-black text-[#111b35]'>
                              {featuredArticle.author}
                            </div>
                            <div className='text-sm font-semibold text-slate-500'>
                              {featuredArticle.role}
                            </div>
                          </div>
                        </div>
                        <span
                          aria-label={`Read ${featuredArticle.title}`}
                          className='inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-[#111b35] transition hover:-translate-y-1 hover:border-[#f59e0b] hover:text-[#f59e0b] hover:shadow-[0_16px_30px_rgba(245,158,11,0.18)]'
                        >
                          <ArrowRight className='h-5 w-5' />
                        </span>
                      </div>
                    </div>
                  </Link>
                ) : (
                  <div className='rounded-[2rem] border border-slate-200 bg-white p-10 text-center shadow-[0_24px_70px_rgba(15,23,42,0.08)]'>
                    <h2 className='text-2xl font-black text-[#111b35]'>
                      No articles found
                    </h2>
                    <p className='mt-3 text-base font-semibold text-slate-600'>
                      Try another topic or search term to browse available
                      articles.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </section>

          {secondaryArticles.length > 0 && (
            <section className='px-5 pb-24 lg:px-8'>
              <div className='w-full'>
                <div className='mb-8 text-center'>
                  <h2 className='text-[2rem] font-black tracking-tight text-[#111b35] md:text-[2.65rem]'>
                    More Articles
                  </h2>
                  <p className='mx-auto mt-3 max-w-2xl text-base font-semibold leading-7 text-slate-600'>
                    Additional coverage and operations insights for imaging
                    centers.
                  </p>
                </div>

                <div className='grid gap-6 md:grid-cols-2 xl:grid-cols-3'>
                  {secondaryArticles.map((article) => (
                    <Link
                      key={article.slug}
                      href={`/blog/${article.slug}`}
                      className='group overflow-hidden rounded-[1.65rem] border border-slate-200 bg-white shadow-[0_16px_42px_rgba(15,23,42,0.06)] transition hover:-translate-y-2 hover:shadow-[0_26px_62px_rgba(15,23,42,0.12)]'
                    >
                      <div className='relative h-56 overflow-hidden bg-slate-200'>
                        <Image
                          src={article.image}
                          alt=''
                          fill
                          sizes='(max-width: 767px) 100vw, (max-width: 1279px) 50vw, 33vw'
                          className='h-full w-full object-cover transition duration-500 group-hover:scale-105'
                        />
                        <div className='absolute left-4 top-4 rounded-full bg-white/92 px-4 py-2 text-xs font-black uppercase tracking-wide text-[#111b35]'>
                          {article.category}
                        </div>
                      </div>
                      <div className='p-6'>
                        <div className='flex items-center gap-3 text-xs font-bold text-slate-500'>
                          <CalendarDays className='h-4 w-4' />
                          {article.date}
                          <span className='text-slate-300'>.</span>
                          {article.readTime}
                        </div>
                        <h3 className='mt-4 text-xl font-black leading-7 tracking-tight text-[#111b35]'>
                          {article.title}
                        </h3>
                        <p className='mt-4 text-sm font-semibold leading-7 text-slate-600'>
                          {article.excerpt}
                        </p>
                        <div className='mt-6 flex items-center justify-between'>
                          <div className='text-sm font-black text-[#111b35]'>
                            {article.author}
                          </div>
                          <ArrowRight className='h-4 w-4 text-[#f59e0b] transition group-hover:translate-x-1' />
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
      </div>
    </SiteLayout>
  );
};

export default BlogPage;
